import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Przycisk.
 *
 * Warianty opisane przez `cva`, a nie przez sklejanie klas w miejscu użycia —
 * dzięki temu „drugorzędny przycisk" jest jedną decyzją w jednym pliku, a nie
 * powtórzoną listą klas w dwudziestu.
 *
 * `asChild` renderuje przez Radix Slot, więc `<Button asChild><Link/></Button>`
 * daje prawdziwy odnośnik z wyglądem przycisku. Bez tego każdy przycisk
 * nawigacyjny musiałby duplikować klasy na `<Link>`, a nawigacja przestałaby
 * działać środkowym przyciskiem myszy i w nowej karcie.
 */
const buttonVariants = cva(
  cn(
    "focus-ring-own inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md text-sm font-medium outline-none transition-all",
    // Pierścień fokusu na komponencie, nie globalny: kolor zależy od wariantu,
    // a wariant niszczący musi świecić na czerwono, nie na akcencie.
    "focus-visible:ring-[0.1875rem] focus-visible:ring-ring/60",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  ),
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        // Gradient zostaje jednym wariantem, nie domyślnym: użyty wszędzie
        // przestaje cokolwiek wyróżniać.
        hero: cn(
          "bg-gradient-to-r from-primary to-accent text-primary-foreground",
          "font-semibold shadow-lg shadow-primary/20 hover:brightness-110",
        ),
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        outline: "border border-border bg-card/40 hover:bg-secondary/60",
        ghost: "hover:bg-secondary/60 hover:text-foreground",
        destructive: cn(
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
          "focus-visible:ring-destructive/60",
        ),
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-lg px-6 text-base has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot : "button";
  return (
    <Component
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
